import { useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

/** Straight Answers (spec 010 §3.5). Plain, honest; an accordion at phone width, an open list from md up. */
export const STRAIGHT_ANSWERS = [
  {
    q: 'Is it really free?',
    a: 'Yes. The diagnostic, practice sets, and your course map are free, no account. A free account saves your progress across days. 1-on-1 tutoring is paid and optional.',
  },
  {
    q: 'Are these real SAT questions?',
    a: "They're written to the digital SAT's format and skills. Full-length practice tests run in College Board's Bluebook; you enter your score.",
  },
  {
    q: 'What does Mentara sell?',
    a: 'Tutoring with a real person, for the SAT and other subjects, booked and paid through the platform.',
  },
  {
    q: 'Do I need an account?',
    a: 'No. Take the diagnostic and practice without one. Nothing is saved, so create a free account to keep your progress.',
  },
] as const;

export function StraightAnswers() {
  const listRef = useRef<HTMLDivElement>(null);
  // Prerendered open (so the answers are in the HTML); collapse to an accordion at phone width on hydrate.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 767px)').matches) return;
    listRef.current?.querySelectorAll('details').forEach((d) => d.removeAttribute('open'));
  }, []);

  return (
    <section aria-labelledby="answers-title" className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 lg:py-16">
      <h2 id="answers-title" className="text-2xl font-semibold tracking-tight sm:text-3xl">
        Straight answers
      </h2>
      <div ref={listRef} className="mt-7 grid gap-x-10 gap-y-2 md:grid-cols-2 md:gap-y-6" data-testid="straight-answers">
        {STRAIGHT_ANSWERS.map(({ q, a }) => (
          <details key={q} open className="group border-b py-1 md:border-b-0 md:py-0">
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded text-base font-semibold outline-none marker:hidden focus-visible:ring-2 focus-visible:ring-primary md:pointer-events-none md:min-h-0 [&::-webkit-details-marker]:hidden">
              {q}
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180 md:hidden" aria-hidden />
            </summary>
            <p className="max-w-prose pb-3 pt-1 text-[15px] leading-relaxed text-muted-foreground md:pb-0">{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
